import i18n from 'i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('Asia/Tokyo');

import 'intl-tel-input/styles';

import TextCopy from '../../../components/TextCopy';
import UseTicketButton from '../../../components/UseTicketButton/UseTicketButton';
import Header from '../../../components/header/Header';
import CenterLoading from '../../../components/CenterLoading/CenterLoading';
import SmallLoading from '../../../components/SmallLoading/SmallLoading';
import TextBreaks from '../../../components/TextBreaks/TextBreaks';

import useLocalizedMoney from '../../../hooks/useLocalizedMoney';
import useLocalizedTime from '../../../hooks/useLocalizedTime';
import useRefundTicket from '../../../hooks/useRefundTicket';
import { CacheManager } from '../../../utils/CacheManager/CacheManager';
import {
    ORDER_STATUS,
    TICKET_STATUS,
    TICKET_TYPE,
    USAGE_TICKET_STATUS,
    DEV_ENV_FLAG,
    SECTION_TYPE,
    DISTRIBUTION_TYPE,
    RESERVATION_CHANGE_TYPE,
    QR_TYPE,
} from '../../../utils/Constants';
import {
    canActivateNow,
    canDisplayNow,
    canDistribution,
    convertTextToLinks,
    getValueByLanguage,
    isChildTicket,
    isComboTicket,
    isDistributed,
    isEmptyObject,
    isNumber,
    isQrCodeScanned,
    isRefunded,
    isRefundPending,
    isReservationCanceled,
    isReserveTicket,
    isSameDeviceId,
    shouldDisplayActivatedAt,
    shouldDisplayRefundDeadlineAt,
    showNotificationDialog,
} from '../../../utils';
import { formatReservationUserInfo } from '../../../utils/calculationUtils';
import CouponTickets from './CouponTickets';
import styles from './styles.module.css';
import useR05Api from '../../../api/useR05Api';
import useChangeTicketBrowser from '../../../hooks/useChangeTicketBrowser';
import { orderTicketUtilTest } from '../../../utils/orderTicketUtilTest';
import AvailableWeekdays from '../../../components/AvailableWeekdays/AvailableWeekdays';
import defaultOptionalForm from '../../../api/data-dummy/default-optional-form.json';
import CustomAntButton from '../../../components/CustomAntButton/CustomAntButton';
import usePurchaseTicket from '../../../hooks/usePurchaseTicket';
import useReservationTicket from '../../../hooks/useReservationTicket';
import { OrderTicket, ReservationInfo } from 'types';

/**
 * 図4-2 使用前の企画券の詳細画面
 */
const BoughtTicketDetailScreen = () => {
    const [orderTicket, setOrderTicket] = useState<OrderTicket>();
    const [loading, setLoading] = useState(false);
    const [smallLoading, setSmallLoading] = useState(false);
    const [topContentInfo, setTopContentInfo] = useState<{
        label?: string;
        value?: string;
        display?: boolean;
    } | null>();
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { formatTime } = useLocalizedTime();
    const { formatMoney } = useLocalizedMoney();

    const { orderId, orderTicketId } = location.state ?? {};
    const { language } = i18n;
    const { loading: refundLoading, checkRefundTicket, canRefundTicket } = useRefundTicket();
    const { fetchOrderDetail, fetchHolidays } = useR05Api();
    const {
        deviceId,
        isLoading: changeDeviceLoading,
        ConfirmationModal: ChangeBrowserConfirmationModal,
        openConfirmationModal,
    } = useChangeTicketBrowser();
    const { distributionsOrderTicket, isLoading: distributionLoading } = usePurchaseTicket();
    const { prepareReservationEdit, isLoading: reservationLoading } = useReservationTicket();

    const reservationInfo: ReservationInfo | null = useMemo(() => {
        if (isEmptyObject(orderTicket)) return null;
        const reservationTicket = orderTicket?.comboItems.find((item) => item.isReserve);
        return reservationTicket?.orderUsageTickets?.[0]?.reservationInfo as ReservationInfo;
    }, [orderTicket]);

    const optionalForm = useMemo(() => {
        const visibleOptionalForm = reservationInfo?.reservationForm?.filter((field: any) => field.isVisible);
        const combined: any = {};
        defaultOptionalForm?.forEach((item) => {
            combined[item.key] = item;
        });
        visibleOptionalForm?.forEach((item: any) => {
            combined[item.key] = item;
        });
        return Object.values(combined);
    }, [reservationInfo]);

    const fetchOrderTicket = useCallback(
        async (orderId: string, orderTicketId: string) => {
            setLoading(true);

            const cacheName = 'bought_ticket_detail_cache';
            const cacheUrl = `/bought_ticket_detail/?orderId=${orderId}&orderTicketId=${orderTicketId}`;
            const boughtTicketDetailCache = new CacheManager(cacheName, cacheUrl);

            // オンライン時のfetch
            const fetchOnline = async () => {
                let data = await fetchOrderDetail({
                    orderId: orderId,
                    orderTicketId: orderTicketId,
                });
                const holidays = await fetchHolidays();
                // 販売チケット名のキーを分離
                data = {
                    ...data,
                    saleTicketName: data?.ticketName,
                    isReserve: isReserveTicket(data),
                    holidays,
                };
                //子券・単券の場合
                if (!isComboTicket(data)) {
                    data = {
                        ...data,
                        ...data.comboItems[0],
                    };
                }
                setOrderTicket(data);
                await boughtTicketDetailCache.saveToCache(data);
            };

            // オフライン or 低速時のfetch
            const fetchOffline = async () => {
                let data = await boughtTicketDetailCache.readFromCache();
                //子券・単券の場合
                if (!isComboTicket(data)) {
                    data = { ...data, ...data.comboItems[0] };
                }
                setOrderTicket(data);
            };

            try {
                await fetchOffline();
            } catch {}
            setLoading(false);

            // オンライン時は、ポップアップのローディングを出して、チケット詳細を更新する
            if (navigator.onLine) {
                setSmallLoading(true);
                try {
                    await fetchOnline();
                } catch {
                    showNotificationDialog(
                        'error',
                        '',
                        t('notification.title'),
                        t('error-message.tickets.fetch-info-failed'),
                    );
                }
                setSmallLoading(false);
            } else {
                showNotificationDialog('error', '', t('notification.title'), t('ticket-display.no-ticket-cache'));
            }
        },
        [fetchHolidays, fetchOrderDetail, t],
    );

    const topContent = useCallback(
        (orderTicket?: OrderTicket) => {
            if (!orderTicket) {
                return null;
            }
            const { orderUsageTickets, status, ticketType } = orderTicket;

            if (isRefundPending(orderTicket)) {
                return { label: '', value: t('ticket-detail.refund-pending'), display: true };
            } else if (isRefunded(orderTicket)) {
                return { label: '', value: t('ticket-detail.refunded'), display: true };
            } else if (status && status === TICKET_STATUS.ACTIVATE_DEADLINE_PASSED) {
                return { label: '', value: t('ticket-detail.expired'), display: true };
            } else if (status && status === TICKET_STATUS.BOUGHT) {
                if (ticketType === TICKET_TYPE.TICKET) {
                    const orderUsageTicketStatus = orderUsageTickets?.[0].status;
                    switch (orderUsageTicketStatus) {
                        case USAGE_TICKET_STATUS.BOUGHT:
                            if (isReserveTicket(orderTicket)) {
                                const reserveOn = reservationInfo?.reserveOn;
                                return {
                                    label: t('reservation.usage-time') + ': ',
                                    value: t('daytime.tilde', { time: formatTime(reserveOn, 'lll') }),
                                    display: !!reserveOn,
                                };
                            } else {
                                const activateDeadlineAt = getTimeValue(orderTicket, 'activateDeadlineAt');
                                return {
                                    label: t('ticket-detail.activate-deadline') + ': ',
                                    value: t('daytime.until', { time: formatTime(activateDeadlineAt, 'lll') }),
                                    display: activateDeadlineAt != null,
                                };
                            }

                        case USAGE_TICKET_STATUS.ACTIVATED:
                            if (isReserveTicket(orderTicket)) {
                                const reserveOn = reservationInfo?.reserveOn;
                                return {
                                    label: t('reservation.usage-time') + ': ',
                                    value: t('daytime.tilde', { time: formatTime(reserveOn, 'lll') }),
                                    display: !!reserveOn,
                                };
                            } else {
                                const expiredAt = getTimeValue(orderTicket, 'expiredAt');
                                return {
                                    label: t('ticket-detail.expired-at') + ': ',
                                    value: t('daytime.until', { time: formatTime(expiredAt, 'lll') }),
                                    display: expiredAt !== null,
                                };
                            }
                        case USAGE_TICKET_STATUS.USED:
                        case USAGE_TICKET_STATUS.EXPIRED:
                            return {
                                label: '',
                                value: t('ticket-detail.used'),
                                display: true,
                            };

                        default:
                            return {};
                    }
                } else {
                    const hasBoughtTicket = orderUsageTickets?.some(
                        (item) => item.status === USAGE_TICKET_STATUS.BOUGHT,
                    );
                    const activateDeadlineAt = getTimeValue(orderTicket, 'activateDeadlineAt');
                    return hasBoughtTicket
                        ? {
                              label: t('ticket-detail.activate-deadline') + ': ',
                              value: t('daytime.until', { time: formatTime(activateDeadlineAt, 'lll') }),
                              display: activateDeadlineAt != null,
                          }
                        : {
                              label: '',
                              value: t('ticket-detail.used'),
                              display: true,
                          };
                }
            } else {
                return {};
            }
        },
        [formatTime, reservationInfo, t],
    );

    useEffect(() => {
        setTopContentInfo(topContent(orderTicket));
    }, [orderTicket, topContent]);

    // チケットが正常に判定されるかチェックする
    if (DEV_ENV_FLAG) {
        orderTicketUtilTest(orderTicket); // TODO:ログが埋もれるので安定したら消す
    }

    /**
     * チケットの情報を取得する
     */
    useEffect(() => {
        void fetchOrderTicket(orderId, orderTicketId);
    }, [orderId, orderTicketId, fetchOrderTicket]);

    const refetch = () => {
        if (!smallLoading) {
            setSmallLoading(true);
            void fetchOrderTicket(orderId, orderTicketId).then(() => {
                setSmallLoading(false);
            });
        }
    };

    //クーポン券使用状況を表示するかどうかチェック
    const isDisplayCouponTickets = (orderTicket: OrderTicket) => {
        const hasUsedCoupon = orderTicket.orderUsageTickets?.some(
            (coupon) => coupon.status === USAGE_TICKET_STATUS.USED,
        );
        return hasUsedCoupon && orderTicket.ticketType === TICKET_TYPE.COUPON;
    };

    /**
     * 指定されたチケット情報とプロパティ名に基づいて、対応する時間値を取得する。
     *
     * @param {OrderTicket} orderTicket - チケット情報を含むオブジェクトです。
     * @param {string} propertyName - 取得したい時間値のプロパティ名（例: 'activateDeadlineAt', 'activatedAt', 'expiredAt'）
     * @returns {string|null} - フォーマットされた時間値または時間値が存在しない場合は null が返されます。
     */
    const getTimeValue = (orderTicket?: OrderTicket, propertyName?: string): Date | string | null => {
        if (!orderTicket || !propertyName || isComboTicket(orderTicket)) {
            return null;
        }
        if (propertyName === 'activateDeadlineAt') {
            //使用期限を取得する
            return orderTicket.activateDeadlineAt;
        } else {
            //有効期限や使用日時を取得する
            if (orderTicket.ticketType === TICKET_TYPE.TICKET) {
                return (orderTicket.orderUsageTickets?.[0] as any)[propertyName];
            } else {
                return null;
            }
        }
    };

    const handleRefundBtnClick = async () => {
        const { isPassed, data } = await checkRefundTicket(orderTicket);
        if (isPassed) {
            navigate('/bought/refund', { state: { orderTicket: data } });
        }
    };

    const { displayCouponTotal, availableCoupon, totalCoupon, usedTotal } = useMemo(() => {
        let displayCouponTotal = false;
        let availableCoupon = 0;
        let totalCoupon = 0;
        let usedTotal = 0;

        if (!orderTicket || !orderTicket.orderUsageTickets) {
            return { displayCouponTotal, availableCoupon, totalCoupon };
        }

        displayCouponTotal = orderTicket.ticketType === TICKET_TYPE.COUPON;
        totalCoupon = orderTicket.orderUsageTickets.length;
        availableCoupon = orderTicket.orderUsageTickets.filter(
            (coupon) => coupon.status === USAGE_TICKET_STATUS.BOUGHT,
        ).length;
        usedTotal = orderTicket.orderUsageTickets.filter((coupon) => coupon.status === USAGE_TICKET_STATUS.USED).length;

        return { displayCouponTotal, availableCoupon, totalCoupon, usedTotal };
    }, [orderTicket]);

    const { listFields } = formatReservationUserInfo(reservationInfo);

    const handleReservationEdit = async () => {
        if (orderTicket) {
            const success = await prepareReservationEdit(orderTicket);
            if (success) {
                navigate(`/buy/reservation/confirm`);
            }
        }
    };

    const handleDistributionsClick = async () => {
        if (orderTicket?.distributionType === DISTRIBUTION_TYPE.REQUIRED) {
            const { remainingNumberAdults, remainingNumberCustom1, remainingNumberCustom2, remainingNumberCustom3 } =
                orderTicket?.remainingOrderDistribution ?? {};
            const orderTicketId = await distributionsOrderTicket({
                orderTicketId: orderTicket?.orderTicketId,
                distributeCount: {
                    numberAdults: remainingNumberAdults ?? 0,
                    numberCustom1: remainingNumberCustom1 ?? 0,
                    numberCustom2: remainingNumberCustom2 ?? 0,
                    numberCustom3: remainingNumberCustom3 ?? 0,
                },
            });
            if (orderTicketId) {
                navigate('/bought/distributions/info', { state: { orderTicketId } });
            }
        } else {
            navigate('/bought/distributions', { state: { orderTicket: orderTicket } });
        }
    };

    const handleDistributionsInfoClick = async () => {
        navigate('/bought/distributions/info', { state: { orderTicketId: orderTicket?.orderTicketId } });
    };

    const isDisplayReserveEditBtn = (orderTicket?: OrderTicket) => {
        if (!orderTicket) {
            return false;
        }
        //セット券の子券場合は変更できない
        if (isChildTicket(orderTicket)) {
            return false;
        }
        const { comboItems, orderStatus, orderDistributionStatus } = orderTicket;
        //該当する個別チケットのキャンセルポリシーが設定されていないこと。
        // if (cancellationFee) {
        //     return false;
        // }

        //予約チケットであること,
        const reservationTicket = comboItems?.find((item) => item.isReserve);
        if (!reservationTicket || isEmptyObject(reservationTicket)) {
            return false;
        }
        //予約キャンセルされたこと
        if (isReservationCanceled(orderTicket)) {
            return false;
        }

        const {
            reservationDisplayStart,
            reservationDisplayStartUnit,
            usingPeriod,
            usingPeriodUnit,
            reservationChangeType,
            qrType,
            orderUsageTickets,
            remainingOrderDistribution,
        } = reservationTicket;
        //予約変更可能である設定が有効になっていること
        if (reservationChangeType !== RESERVATION_CHANGE_TYPE.NO_PRICE_CHANGE) {
            return false;
        }

        const remainingOrderDistributionStatus = remainingOrderDistribution?.remainingOrderDistributionStatus;

        // QRコード認証済みの場合は予約変更不可
        if (isQrCodeScanned(remainingOrderDistributionStatus, orderDistributionStatus)) {
            return false;
        }

        //チケットを含むチケットの購入状態が「購入済み」であること、券片の使用状態がすべて「購入済み」であること
        const isAllBoughtTicket =
            orderStatus === ORDER_STATUS.BOUGHT &&
            comboItems?.every(
                (item) =>
                    item.status === TICKET_STATUS.BOUGHT &&
                    item.orderUsageTickets?.every((ticket) => ticket.status === USAGE_TICKET_STATUS.BOUGHT),
            );
        if (!isAllBoughtTicket) {
            return false;
        }
        // 操作時刻の取得
        const now = dayjs.tz();
        const reservationInfo = orderUsageTickets?.[0]?.reservationInfo;
        const reserveOn = reservationInfo?.reserveOn ?? (orderUsageTickets?.[0] as any).reserveOn;
        //QR券面ではない場合、予約券表示開始日時のうち、最も短いもの
        if (qrType === QR_TYPE.DISABLE) {
            const compareReserveUnit = reservationDisplayStartUnit === 'minutes' ? 'minute' : 'day';
            const reservationDisplayStartTime = dayjs(reserveOn)
                .subtract(reservationDisplayStart ?? 0, compareReserveUnit)
                .startOf(compareReserveUnit);
            const isAfterReservationDisplayStartTime = now.isAfter(reservationDisplayStartTime);
            if (isAfterReservationDisplayStartTime) {
                return false;
            }
        } else {
            //QR券面(1回読み取り)の場合、予約券表示終了日時のうち、最も短いもの
            const compareUsingUnit = usingPeriodUnit === 'm' ? 'minute' : 'day';
            const reservationDisplayEndTime =
                compareUsingUnit === 'day'
                    ? dayjs(reserveOn)
                          .add(usingPeriod ?? 0, compareUsingUnit)
                          .set('hour', 3)
                          .set('minute', 59)
                          .set('second', 59)
                    : dayjs(reserveOn).add(usingPeriod ?? 0, compareUsingUnit);
            const isAfterReservationDisplayEndTime = now.isAfter(reservationDisplayEndTime);
            if (isAfterReservationDisplayEndTime) {
                return false;
            }
        }

        return true;
    };

    const distributedNumber = useMemo(() => {
        const {
            numberAdults = 0,
            numberCustom1 = 0,
            numberCustom2 = 0,
            numberCustom3 = 0,
            remainingOrderDistribution,
        } = orderTicket ?? {};
        const { remainingNumberAdults, remainingNumberCustom1, remainingNumberCustom2, remainingNumberCustom3 } =
            remainingOrderDistribution ?? {};

        const distributedNumberAdults =
            numberAdults !== 0 && remainingNumberAdults !== null ? numberAdults - (remainingNumberAdults ?? 0) : null;
        const distributedNumberCusTom1 =
            numberCustom1 !== 0 && remainingNumberCustom1 !== null
                ? numberCustom1 - (remainingNumberCustom1 ?? 0)
                : null;
        const distributedNumberCusTom2 =
            numberCustom2 !== 0 && remainingNumberCustom2 !== null
                ? numberCustom2 - (remainingNumberCustom2 ?? 0)
                : null;
        const distributedNumberCusTom3 =
            numberCustom3 !== 0 && remainingNumberCustom3 !== null
                ? numberCustom3 - (remainingNumberCustom3 ?? 0)
                : null;

        return {
            distributedNumberAdults,
            distributedNumberCusTom1,
            distributedNumberCusTom2,
            distributedNumberCusTom3,
        };
    }, [orderTicket]);

    const isDisplayUseBtn = (orderTicket: OrderTicket) => {
        if (isComboTicket(orderTicket)) {
            return false;
        }
        if (orderTicket.distributionType === DISTRIBUTION_TYPE.REQUIRED && canActivateNow(orderTicket)) {
            return false;
        }
        if (
            orderTicket.distributionType === DISTRIBUTION_TYPE.ENABLE &&
            isDistributed(orderTicket) &&
            canActivateNow(orderTicket)
        ) {
            return false;
        }
        return true;
    };

    const refundDeadline = useMemo(() => {
        const { orderStatus, refundDeadlineAt, purchasedAt } = orderTicket ?? {};
        if (
            orderStatus === ORDER_STATUS.USER_REFUND_ENABLED ||
            orderStatus === ORDER_STATUS.STOCK_USER_REFUND_ENABLED
        ) {
            return dayjs(purchasedAt).add(365, 'day').format();
        } else {
            return refundDeadlineAt;
        }
    }, [orderTicket]);

    const formatReservationFieldValue = (key?: string, value?: string | number) => {
        if (value == null || value === '') return null;

        if (String(value).includes('option=')) {
            const field: any = optionalForm.find((field: any) => field.key === key && field.type === 'enum');
            if (!field) {
                return null;
            }
            return getValueByLanguage(
                field.options?.find((option: any) => String(option.optionKey) === String(value).replace('option=', '')),
                language,
            );
        }

        return value;
    };

    return (
        <>
            {(loading || refundLoading || changeDeviceLoading || distributionLoading || reservationLoading) && (
                <CenterLoading />
            )}
            <SmallLoading show={smallLoading} content={t('loading.small-bought-ticket') ?? ''} />
            <Header title={t('header.ticket-detail')} refetch={refetch} />
            <ChangeBrowserConfirmationModal orderTicket={orderTicket} />
            <div className={`${styles.wrapper} content-container scrollable`}>
                {orderTicket ? (
                    <div className={styles.ticketInfo}>
                        {/*上部表示領域*/}
                        {topContentInfo?.display && (
                            <div className={styles.frame}>
                                {(topContentInfo?.label ?? '') + (topContentInfo?.value ?? '')}
                            </div>
                        )}
                        <div className={styles.infoGroup}>
                            {orderTicket?.comboItems?.length > 1 ? (
                                <>
                                    {/* 親券の場合 */}
                                    {/* 販売権種名*/}
                                    <div className={styles.ticketName}>
                                        <TextBreaks text={getValueByLanguage(orderTicket?.saleTicketName, language)} />
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* 子券・単券の場合*/}
                                    {/* 販売権種名*/}
                                    <div className={styles.saleTicketName}>
                                        <TextBreaks text={getValueByLanguage(orderTicket?.saleTicketName, language)} />
                                    </div>
                                    {/*個別チケット名*/}
                                    <div className={styles.ticketName}>
                                        <TextBreaks text={getValueByLanguage(orderTicket.ticketName, language)} />
                                    </div>
                                </>
                            )}
                            {/*サブタイトル*/}
                            <div className={styles.subtitle}>
                                <TextBreaks text={getValueByLanguage(orderTicket.subtitle, language)} />
                            </div>
                            {/*使用開始可能曜日*/}
                            {!isComboTicket(orderTicket) && isEmptyObject(reservationInfo) && (
                                <AvailableWeekdays
                                    ticket={orderTicket}
                                    className={styles.textContent}
                                    labelClass={styles.label}
                                    dividerClass={styles.divider}
                                    valueClass={styles.value}
                                />
                            )}
                            {/* 回数券枚数 */}
                            {displayCouponTotal && (
                                <div className={styles.textContent}>
                                    {availableCoupon > 0
                                        ? `${t('ticket-detail.before-use')}: ${t('ticket-detail.coupon-count', {
                                              count: availableCoupon,
                                          })}(${t('ticket-detail.coupon-amount')}${t('ticket-detail.coupon-count', {
                                              count: totalCoupon,
                                          })})`
                                        : `${t('ticket-detail.used')}: ${t('ticket-detail.coupon-count', {
                                              count: usedTotal,
                                          })}`}
                                </div>
                            )}
                        </div>
                        {/*予約情報*/}
                        {!isEmptyObject(reservationInfo) && (
                            <>
                                {/*セット券場合は在庫情報を非表示*/}
                                {!isComboTicket(orderTicket) && (
                                    <div className={styles.infoGroup}>
                                        <p className={styles.descriptions}>
                                            <TextBreaks
                                                text={getValueByLanguage(reservationInfo?.stockPartitionName, language)}
                                            />
                                        </p>
                                        <p className={styles.descriptions}>
                                            <TextBreaks
                                                text={getValueByLanguage(
                                                    reservationInfo?.reservationClassName,
                                                    language,
                                                )}
                                            />
                                            {!getValueByLanguage(reservationInfo?.reservationClassName, language) ||
                                            !getValueByLanguage(reservationInfo?.reservationPriceDescription, language)
                                                ? ''
                                                : '/ '}

                                            <TextBreaks
                                                text={getValueByLanguage(
                                                    reservationInfo?.reservationPriceDescription,
                                                    language,
                                                )}
                                            />
                                        </p>
                                        {orderTicket.sectionType === SECTION_TYPE.MULTI && (
                                            <p className={styles.descriptions}>
                                                <TextBreaks
                                                    text={getValueByLanguage(reservationInfo?.originStopName, language)}
                                                />
                                                <span style={{ margin: '0 5px' }}>→</span>
                                                <TextBreaks
                                                    text={getValueByLanguage(reservationInfo?.destStopName, language)}
                                                />
                                            </p>
                                        )}
                                        <p className={styles.descriptions}>
                                            <TextBreaks
                                                text={getValueByLanguage(
                                                    reservationInfo?.stockPartitionDescription,
                                                    language,
                                                )}
                                            />
                                        </p>
                                        {!!reservationInfo?.reserveOn && (
                                            <div className={styles.textContent}>
                                                <span className={styles.label}>{t('reservation.usage-time')}</span>
                                                <span className={styles.divider}>:</span>
                                                <div className={styles.value}>
                                                    {formatTime(reservationInfo?.reserveOn, 'lll')}
                                                </div>
                                            </div>
                                        )}
                                        {!!reservationInfo?.reservationId && (
                                            <div className={styles.textContent}>
                                                <span className={styles.label}>{t('reservation.reservation-id')}</span>
                                                <span className={styles.divider}>:</span>
                                                <div className={styles.value}>{reservationInfo?.reservationId}</div>
                                            </div>
                                        )}
                                        {!!reservationInfo?.reservationVerifyCode && (
                                            <div className={styles.textContent}>
                                                <span className={styles.label}>
                                                    {t('reservation.reservation-verify-code')}
                                                </span>
                                                <span className={styles.divider}>:</span>
                                                <div className={styles.value}>
                                                    {reservationInfo?.reservationVerifyCode}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {/*予約者情報*/}
                                <div className={styles.infoGroup}>
                                    {listFields.map(({ label, value, key }) => {
                                        const formatValue = formatReservationFieldValue(key, value);
                                        if (formatValue == null && formatValue !== 0) {
                                            return '';
                                        }
                                        return (
                                            <div key={key} className={styles.textContent}>
                                                <span className={styles.label}>
                                                    {getValueByLanguage(label, language)}
                                                </span>
                                                <span className={styles.divider}>:</span>
                                                <div className={styles.value}>
                                                    {key === 'phoneNumber'
                                                        ? reservationInfo?.userForm?.phoneNumber
                                                        : formatValue}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                        <div className={styles.infoGroup}>
                            {/*使用日時*/}
                            {getTimeValue(orderTicket, 'activatedAt') != null &&
                                shouldDisplayActivatedAt(orderTicket) && (
                                    <div className={styles.textContent}>
                                        <span className={styles.label}>{t('ticket-detail.activated-date')}</span>
                                        <span className={styles.divider}>:</span>
                                        <div className={styles.value}>
                                            {formatTime(getTimeValue(orderTicket, 'activatedAt'), 'lll')}
                                        </div>
                                    </div>
                                )}
                            {/*購入日時*/}
                            <div className={styles.textContent}>
                                <span className={styles.label}>{t('ticket-detail.purchased-date')}</span>
                                <span className={styles.divider}>:</span>
                                <div className={styles.value}>{formatTime(orderTicket.purchasedAt, 'lll')}</div>
                            </div>
                            {/*人数*/}
                            <div className={styles.textContent}>
                                <span className={styles.label}>{t('ticket-detail.number-label')}</span>
                                <span className={styles.divider}>:</span>
                                <div className={styles.value}>
                                    {orderTicket.numberAdults > 0 && (
                                        <span className={styles.numberContext}>
                                            {t('ticket-detail.number', {
                                                label: t('ticket-detail.adult-label'),
                                                number: orderTicket.numberAdults,
                                            })}
                                        </span>
                                    )}
                                    {orderTicket.numberCustom1 > 0 && (
                                        <span className={styles.numberContext}>
                                            {t('ticket-detail.number', {
                                                label: getValueByLanguage(orderTicket.custom1Name, language),
                                                number: orderTicket.numberCustom1,
                                            })}
                                        </span>
                                    )}
                                    {orderTicket.numberCustom2 > 0 && (
                                        <span className={styles.numberContext}>
                                            {t('ticket-detail.number', {
                                                label: getValueByLanguage(orderTicket.custom2Name, language),
                                                number: orderTicket.numberCustom2,
                                            })}
                                        </span>
                                    )}
                                    {orderTicket.numberCustom3 > 0 && (
                                        <span className={styles.numberContext}>
                                            {t('ticket-detail.number', {
                                                label: getValueByLanguage(orderTicket.custom3Name, language),
                                                number: orderTicket.numberCustom3,
                                            })}
                                        </span>
                                    )}
                                </div>
                            </div>
                            {/*分配済み人数*/}
                            {(orderTicket.distributionType === DISTRIBUTION_TYPE.ENABLE ||
                                orderTicket.distributionType === DISTRIBUTION_TYPE.REQUIRED) && (
                                <div className={styles.textContent}>
                                    <span className={styles.label}>{t('ticket-detail.distributed-number-label')}</span>
                                    <span className={styles.divider}>:</span>
                                    <div className={styles.value}>
                                        {distributedNumber.distributedNumberAdults !== null && (
                                            <span className={styles.numberContext}>
                                                {t('ticket-detail.number', {
                                                    label: t('ticket-detail.adult-label'),
                                                    number: distributedNumber.distributedNumberAdults,
                                                })}
                                            </span>
                                        )}
                                        {distributedNumber.distributedNumberCusTom1 !== null && (
                                            <span className={styles.numberContext}>
                                                {t('ticket-detail.number', {
                                                    label: getValueByLanguage(orderTicket.custom1Name, language),
                                                    number: distributedNumber.distributedNumberCusTom1,
                                                })}
                                            </span>
                                        )}
                                        {distributedNumber.distributedNumberCusTom2 !== null && (
                                            <span className={styles.numberContext}>
                                                {t('ticket-detail.number', {
                                                    label: getValueByLanguage(orderTicket.custom2Name, language),
                                                    number: distributedNumber.distributedNumberCusTom2,
                                                })}
                                            </span>
                                        )}
                                        {distributedNumber.distributedNumberCusTom3 !== null && (
                                            <span className={styles.numberContext}>
                                                {t('ticket-detail.number', {
                                                    label: getValueByLanguage(orderTicket.custom3Name, language),
                                                    number: distributedNumber.distributedNumberCusTom3,
                                                })}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/*料金*/}
                            {isNumber(orderTicket.amount) && (
                                <div className={styles.textContent}>
                                    <span className={styles.label}>{t('ticket-detail.price')}</span>
                                    <span className={styles.divider}>:</span>
                                    <div className={styles.value}>{formatMoney(orderTicket.amount, 'JPY')}</div>
                                </div>
                            )}
                            {/*払戻期限*/}
                            {refundDeadline != null && shouldDisplayRefundDeadlineAt(orderTicket) && (
                                <div className={styles.textContent}>
                                    <span className={styles.label}>{t('ticket-detail.refund-deadline')}</span>
                                    <span className={styles.divider}>:</span>
                                    <div className={styles.value}>
                                        {t('daytime.until', { time: formatTime(refundDeadline, 'lll') })}
                                    </div>
                                </div>
                            )}
                            {/*払い戻し日時*/}
                            {orderTicket.refundedAt && isRefunded(orderTicket) && (
                                <div className={styles.textContent}>
                                    <span className={styles.label}>{t('ticket-detail.refunded-date')}</span>
                                    <span className={styles.divider}>:</span>
                                    <div className={styles.value}>{formatTime(orderTicket.refundedAt, 'lll')}</div>
                                </div>
                            )}
                        </div>
                        {isRefunded(orderTicket) && !isChildTicket(orderTicket) && (
                            <div className={styles.infoGroup}>
                                {isNumber(orderTicket.amount) && (
                                    <div className={styles.textContent}>
                                        <span className={styles.label}>{t('ticket-detail.salesAmount')}</span>
                                        <span className={styles.divider}>:</span>
                                        <div className={styles.value}>{formatMoney(orderTicket.amount, 'JPY')}</div>
                                    </div>
                                )}
                                {isNumber(orderTicket.refundFee) && (
                                    <div className={styles.textContent}>
                                        <span className={styles.label}>{t('ticket-detail.refundFee')}</span>
                                        <span className={styles.divider}>:</span>
                                        <div className={styles.value}>{formatMoney(orderTicket.refundFee, 'JPY')}</div>
                                    </div>
                                )}
                                {isReserveTicket(orderTicket) && isNumber(orderTicket.cancellationFee) && (
                                    <div className={styles.textContent}>
                                        <span className={styles.label}>{t('ticket-detail.cancellationFee')}</span>
                                        <span className={styles.divider}>:</span>
                                        <div className={styles.value}>
                                            {formatMoney(orderTicket.cancellationFee, 'JPY')}
                                        </div>
                                    </div>
                                )}
                                {isNumber(orderTicket.refundAmount) && (
                                    <div className={styles.textContent}>
                                        <span className={styles.label}>{t('ticket-detail.refundAmount')}</span>
                                        <span className={styles.divider}>:</span>
                                        <div className={styles.value}>
                                            {formatMoney(orderTicket.refundAmount, 'JPY')}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        <div className={styles.infoGroup}>
                            {/*チケット番号*/}
                            <div className={styles.textContent}>
                                <span className={styles.label}>{t('ticket-detail.orderId')}</span>
                                <span className={styles.divider}>:</span>
                                <div className={styles.value}>
                                    <TextCopy>{orderTicket.orderId}</TextCopy>
                                </div>
                            </div>
                        </div>
                        {/*eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing*/}
                        {(isDisplayCouponTickets(orderTicket) ||
                            getValueByLanguage(orderTicket.note, language) !== '') && (
                            <div className={styles.infoGroup}>
                                <div className={styles.textContent}>
                                    {isDisplayCouponTickets(orderTicket) && (
                                        <CouponTickets couponTickets={orderTicket.orderUsageTickets ?? []} />
                                    )}
                                </div>
                                {orderTicket.note && (
                                    <div className={styles.textContent}>
                                        <p className={styles.textNote}>
                                            {convertTextToLinks(getValueByLanguage(orderTicket.note, language))}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                        {isDisplayUseBtn(orderTicket) && <UseTicketButton buttonType="detail" ticket={orderTicket} />}
                        {/*予約更新*/}
                        {isDisplayReserveEditBtn(orderTicket) && (
                            <CustomAntButton
                                type="outlined"
                                className={styles.filledBtn}
                                block
                                onClick={handleReservationEdit}
                            >
                                {t('button.change-reservation')}
                            </CustomAntButton>
                        )}
                        {canDisplayNow(orderTicket) && !isSameDeviceId(orderTicket, deviceId) && (
                            <>
                                <CustomAntButton className={styles.filledBtn} block onClick={openConfirmationModal}>
                                    {t('button.change-browser')}
                                </CustomAntButton>
                                <div
                                    className={styles.textNote}
                                    style={{ color: '#ff1104', fontSize: 14, marginTop: 4 }}
                                >
                                    {t('ticket-detail.change-browser-require-time')}
                                </div>
                            </>
                        )}
                        {canRefundTicket(orderTicket).canRefund && (
                            <CustomAntButton
                                type="outlined"
                                className={styles.refundBtn}
                                block
                                onClick={handleRefundBtnClick}
                            >
                                {t('button.refund')}
                            </CustomAntButton>
                        )}
                        {canDistribution(orderTicket) && (
                            <CustomAntButton
                                type="outlined"
                                className={styles.filledBtn}
                                block
                                onClick={handleDistributionsClick}
                            >
                                {t('button.distributions')}
                            </CustomAntButton>
                        )}
                        {(orderTicket?.distributeCount ?? 0) > 0 && (
                            <CustomAntButton
                                type="outlined"
                                className={styles.filledBtn}
                                block
                                onClick={handleDistributionsInfoClick}
                            >
                                {t('header.distributions-info')}
                            </CustomAntButton>
                        )}
                    </div>
                ) : (
                    <NoTicket isLoading={smallLoading} />
                )}
            </div>
        </>
    );
};

const NoTicket = ({ isLoading }: { isLoading: boolean }) => {
    const { t } = useTranslation();
    return (
        <div className="no-ticket-view">
            {navigator.onLine
                ? isLoading
                    ? t('loading.small-bought-ticket')
                    : t('ticket-display.no-ticket')
                : t('ticket-display.no-ticket-cache')}
        </div>
    );
};

export default BoughtTicketDetailScreen;
